import React from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import {Panel, Row, Col} from 'react-bootstrap';
import {IconButton, Button, Tooltip} from '@material-ui/core';
import {Edit as EditIcon, DeleteForever as DeleteForeverIcon, Clear as ClearIcon} from '@material-ui/icons';
import {isEmpty} from 'lodash/lang';
import {CommonModal, DeletionConfirmBox, ImageWrapper} from 'components/common';
import {SecondaryNavigation} from 'components/Navigation';
import UserTable from 'components/User/UserTable';
import UserCreation from 'components/User/UserCreation';
import ClassroomTable from 'components/Classroom/ClassroomTable';
import ClassroomCreation from 'components/Classroom/ClassroomCreation';
import SchoolUpdate from './SchoolUpdate';
import ArchivedStatus from '../common/ArchivedStatus';
import SubscriptionOverview from '../Subscription/SubscriptionOverview';
import {deleteSchool, removeUserFromSchool, unallocateUnusedSubscriptions} from 'api';
import {
  fetchSchool,
  setCurrentSchool,
  fetchSchoolFaculties,
  setCurrentSchoolFacultyPage,
  setCurrentSchoolFaculty,
  clearSchoolFacultiesAfterPage,
} from 'actions/schools';
import {
  fetchClassrooms,
  setCurrentClassroomPage,
  setCurrentClassroom,
  clearClassroomsAfterPage,
} from 'actions/classrooms';
import {fetchMessages, setCurrentMessagePage, setCurrentMessage, clearMessagesAfterPage} from 'actions/messages';
import {addFlashMessage} from 'actions/flash-message';
import {formatErrors} from 'actions/errors';
import * as permissions from 'utils/permissions';
import {normalizeAddress} from 'utils/common';

class SchoolDetail extends React.Component {
  static contextTypes = {
    router: PropTypes.object.isRequired,
  };
  static propTypes = {
    school: PropTypes.object.isRequired,
    schoolFacultyPages: PropTypes.object.isRequired,
  };

  constructor(props) {
    super(props);
    this.state = {
      updateModalShown: false,
      createClassroomModalShown: false,
      classroomTableKey: 0,
      classroomTableHardRefresh: false,
      createFacultyModalShown: false,
      userTableKey: 0,
    };
    this.fetchSchool = this.fetchSchool.bind(this);
    this.showModal = this.showModal.bind(this);
    this.closeModal = this.closeModal.bind(this);
    this.createClassroomSuccess = this.createClassroomSuccess.bind(this);
    this.createFacultySuccess = this.createFacultySuccess.bind(this);
    this.deleteSchool = this.deleteSchool.bind(this);
    this.hardDeleteSchool = this.hardDeleteSchool.bind(this);
    this.removeUserFromSchool = this.removeUserFromSchool.bind(this);
    this.unallocateUnusedSubscriptions = this.unallocateUnusedSubscriptions.bind(this);
  }

  componentDidMount() {
    this.fetchSchool();
  }

  componentWillUnmount() {
    this.props.setCurrentSchool(null, {});
  }

  fetchSchool() {
    const {schoolId, fetchSchool, formatErrors} = this.props;
    fetchSchool(schoolId).catch((err) => formatErrors(err, '/schools'));
  }

  showModal(type) {
    this.setState({
      [`${type}ModalShown`]: true,
    });
  }

  closeModal(type) {
    this.setState({
      [`${type}ModalShown`]: false,
    });
  }

  createClassroomSuccess() {
    this.setState((prevState) => ({
      classroomTableKey: prevState.classroomTableKey + 1,
      createClassroomModalShown: false,
    }));
  }

  createFacultySuccess() {
    this.setState((prevState) => ({
      userTableKey: prevState.userTableKey + 1,
      createFacultyModalShown: false,
    }));
  }

  deleteSchool() {
    const {schoolId, addFlashMessage, formatErrors} = this.props;
    return deleteSchool(schoolId)
      .then(() => {
        addFlashMessage('The school has been successfully deleted!', 'success');
        this.context.router.history.push('/schools');
      })
      .catch((err) => formatErrors(err));
  }

  hardDeleteSchool() {
    const {schoolId, addFlashMessage, formatErrors} = this.props;
    return deleteSchool(schoolId, true)
      .then(() => {
        addFlashMessage('The school and its users have been successfully deleted!', 'success');
        this.context.router.history.push('/schools');
      })
      .catch((err) => formatErrors(err));
  }

  removeUserFromSchool(user) {
    const {schoolId, addFlashMessage, formatErrors} = this.props;
    removeUserFromSchool(schoolId, user.id)
      .then(() => {
        this.setState(
          (prevState) => ({
            classroomTableKey: prevState.classroomTableKey + 1,
            classroomTableHardRefresh: true,
            userTableKey: prevState.userTableKey + 1,
          }),
          this.setState({
            classroomTableHardRefresh: false,
          }),
        );
        addFlashMessage('The user has been removed from the school!', 'success');
      })
      .catch((err) => formatErrors(err));
  }

  unallocateUnusedSubscriptions() {
    const {schoolId, addFlashMessage, formatErrors} = this.props;
    return unallocateUnusedSubscriptions(schoolId)
      .then(() => {
        this.fetchSchool();
        addFlashMessage('Unused subscriptions of the school has been successfully unallocated!', 'success');
      })
      .catch((err) => formatErrors(err));
  }

  render() {
    const {
      updateModalShown,
      createClassroomModalShown,
      classroomTableKey,
      classroomTableHardRefresh,
      createFacultyModalShown,
      userTableKey,
    } = this.state;
    const {
      isInMySchool,
      schoolId,
      me,
      school,
      classroomPages,
      fetchClassrooms,
      setCurrentClassroomPage,
      setCurrentClassroom,
      clearClassroomsAfterPage,
      schoolFacultyPages,
      fetchSchoolFaculties,
      setCurrentSchoolFacultyPage,
      setCurrentSchoolFaculty,
      clearSchoolFacultiesAfterPage,
    } = this.props;
    const closeUpdateModal = () => this.closeModal('update');
    const baseUrl = isInMySchool ? '/' : `/schools/${schoolId}`;
    const currentUrl = baseUrl === '/' ? '/bulk-invite' : `${baseUrl}/bulk-invite`;
    return (
      <React.Fragment>
        <SecondaryNavigation
          tabs={
            !permissions.isKluwellAdmin(me.role)
              ? [{url: baseUrl, label: 'School Detail', isActive: true}]
              : [
                  {url: baseUrl, label: 'School Detail', isActive: true},
                  {url: currentUrl, label: 'Bulk Invite', isActive: false},
                ]
          }
          hasBack={!isInMySchool}
        />
        <div className="container content-box">
          <Panel>
            <CommonModal title="Edit School" onClose={closeUpdateModal} show={updateModalShown}>
              <SchoolUpdate school={school} onSubmitSuccess={closeUpdateModal} />
            </CommonModal>
            <h3 className="section-header">
              <div className="section-title">School Information</div>
              <div>
                {permissions.isKluwellAdmin(me.role) && (
                  <React.Fragment>
                    <DeletionConfirmBox
                      itemName="school with all its users"
                      deleteAction={this.hardDeleteSchool}
                      DeleteButton={(props) => (
                        <Tooltip title="Delete school with all its users" placement="top">
                          <IconButton variant="contained" color="primary" {...props}>
                            <ClearIcon className="text-red" />
                          </IconButton>
                        </Tooltip>
                      )}
                    />
                    <DeletionConfirmBox
                      itemName="school"
                      deleteAction={this.deleteSchool}
                      DeleteButton={(props) => (
                        <IconButton variant="contained" color="primary" {...props}>
                          <DeleteForeverIcon />
                        </IconButton>
                      )}
                    />
                  </React.Fragment>
                )}
                {permissions.canManageSchool(me.role) && (
                  <IconButton variant="contained" color="primary" onClick={() => this.showModal('update')}>
                    <EditIcon />
                  </IconButton>
                )}
              </div>
            </h3>
            <div className="flex-row-space-between">
              <div className="table-block">
                <div className="table-row">
                  <div className="table-cell">Name</div>
                  <div className="table-cell">{school.name}</div>
                </div>
                <div className="table-row">
                  <div className="table-cell">Phone Number</div>
                  <div className="table-cell">{school.phoneNumber}</div>
                </div>
                <div className="table-row">
                  <div className="table-cell">Address</div>
                  <div className="table-cell">{normalizeAddress(school)}</div>
                </div>
                <div className="table-row">
                  <div className="table-cell">Status</div>
                  <div className="table-cell">{!isEmpty(school) && <ArchivedStatus isActive={school.isActive} />}</div>
                </div>
              </div>
              {school.logo && (
                <ImageWrapper width={150} height={150}>
                  <img alt="Logo" src={`${process.env.SERVER_BASE_URL}${school.logo}`} />
                </ImageWrapper>
              )}
            </div>
            {permissions.isKluwellAdmin(me.role) && (
              <div className="notes">
                <h3>Notes</h3>
                <div className="notes-text">{school.notes ? school.notes : 'There are no notes for this school.'}</div>
              </div>
            )}
          </Panel>

          {!isEmpty(school) && (permissions.isKluwellAdmin(me.role) || permissions.isSchoolCoordinator(me.role)) && (
            <Panel>
              <SubscriptionOverview
                urlPrefix={permissions.isSchoolCoordinatorOrTeacher(me.role) ? '' : `/schools/${schoolId}`}
                activatedSubscriptions={school.activatedSubscriptions}
                pendingSubscriptions={school.pendingSubscriptions}
                allocatedSubscriptions={school.allocatedSubscriptions}
                purchasedSubscriptions={school.purchasedSubscriptions}
                unallocateUnusedSubscriptions={
                  permissions.isKluwellAdmin(me.role) ? this.unallocateUnusedSubscriptions : undefined
                }
              />
            </Panel>
          )}

          <Panel>
            <CommonModal
              title="Create New Classroom"
              onClose={() => this.closeModal('createClassroom')}
              show={createClassroomModalShown}
            >
              <ClassroomCreation schoolId={schoolId} onSubmitSuccess={this.createClassroomSuccess} />
            </CommonModal>
            <h3 className="section-header">
              <div className="section-title">Classrooms</div>
              {permissions.canManageSchool(me.role) && (
                <Button variant="contained" color="primary" onClick={() => this.showModal('createClassroom')}>
                  Create Classroom
                </Button>
              )}
            </h3>
            <ClassroomTable
              {...{
                refreshTableKey: classroomTableKey,
                isHardRefresh: classroomTableHardRefresh,
                recordPages: classroomPages,
                fetchRecords: (q, page, perPage, sortOpts, extraFilters = {}) =>
                  fetchClassrooms(q, page, perPage, sortOpts, {...extraFilters, summary: true, schoolId}),
                setCurrentRecordPage: setCurrentClassroomPage,
                setCurrentRecord: setCurrentClassroom,
                clearRecordsAfterPage: clearClassroomsAfterPage,
                hiddenFields: ['schoolName'],
                extraFiltersEnabled: true,
              }}
            />
          </Panel>

          <Panel>
            <CommonModal
              title="Create School Staff"
              onClose={() => this.closeModal('createFaculty')}
              show={createFacultyModalShown}
            >
              <UserCreation schoolId={schoolId} onSubmitSuccess={this.createFacultySuccess} />
            </CommonModal>
            <h3 className="section-header">
              <div className="section-title">School Staff</div>
              {!permissions.isTeacher(me.role) && (
                <Button variant="contained" color="primary" onClick={() => this.showModal('createFaculty')}>
                  Create School Staff
                </Button>
              )}
            </h3>
            <UserTable
              {...{
                refreshTableKey: userTableKey,
                recordPages: schoolFacultyPages,
                fetchRecords: (...args) => fetchSchoolFaculties(schoolId, ...args),
                setCurrentRecordPage: setCurrentSchoolFacultyPage,
                setCurrentRecord: setCurrentSchoolFaculty,
                clearRecordsAfterPage: clearSchoolFacultiesAfterPage,
                onDeleteActionClick: this.removeUserFromSchool,
                deletionAlertText: 'Please confirm that you wish to remove this user from school',
                me,
                hiddenFields: permissions.isTeacher(me.role) ? ['actions'] : [],
              }}
            />
          </Panel>
        </div>
      </React.Fragment>
    );
  }
}

export default connect(
  (state, ownProps) => {
    const {match} = ownProps;
    const {account, schools, classrooms} = state;
    const user = account.get('user').toJS();
    const schoolId = parseInt(isEmpty(user.activeSchool) ? match.params.schoolId : user.activeSchool.id);
    return {
      isInMySchool: !isEmpty(user.activeSchool) && user.activeSchool.id === schoolId,
      schoolId,
      me: user,
      school: schools.get('currentSchool').toJS(),
      schoolFacultyPages: schools.get('schoolFacultyPages').toJS(),
      classroomPages: classrooms.get('classroomPages').toJS(),
    };
  },
  {
    addFlashMessage,
    formatErrors,
    fetchSchool,
    setCurrentSchool,
    fetchSchoolFaculties,
    setCurrentSchoolFacultyPage,
    setCurrentSchoolFaculty,
    clearSchoolFacultiesAfterPage,
    fetchClassrooms,
    setCurrentClassroomPage,
    setCurrentClassroom,
    clearClassroomsAfterPage,
    fetchMessages,
    setCurrentMessagePage,
    setCurrentMessage,
    clearMessagesAfterPage,
  },
)(SchoolDetail);
